import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Radio, Bus, Home, Building2, Link2, ClipboardList, Target, Loader2, Smartphone, Truck, Circle, Pause, Square, ShieldAlert, XCircle } from 'lucide-react';
import {
  getMySessions,
  TrackingSessionResponse,
  StartTrackingRequest,
  StartDakoTrackingRequest,
  DakoStatusResponse,
  getActiveSessionForPlanning,
  getAdminActiveSessions,
  adminForceStopSession,
  getDakoStatus,
} from '../services/trackingApi';
import { matchdayPlanningApi } from '../services/matchdayPlanningApi';
import { MatchdayPlanning, PlanningSegment } from '../types/matchdayPlanning';
import { useTracking } from '../contexts/TrackingContext';
import { useAuth } from '../contexts/AuthContext';

const Tracking: React.FC = () => {
  // Global tracking context – GPS + session state lives here
  const {
    activeSession, isOwnSession, activeSegments, isSending, lastPosition, posCount,
    targetUpdating, error: ctxError,
    handleStartSession, handleStartDakoSession, handleStopSession, handleChangeTarget, clearError,
  } = useTracking();

  // Local UI state only
  const [sessions, setSessions] = useState<TrackingSessionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [plannings, setPlannings] = useState<MatchdayPlanning[]>([]);
  const [selectedPlanningId, setSelectedPlanningId] = useState<number | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(null);
  const [segments, setSegments] = useState<PlanningSegment[]>([]);
  const [sessionInterval, setSessionInterval] = useState(2);
  const [sessionKeepScreenOn, setSessionKeepScreenOn] = useState(true);
  // Tracking source selection for a new session: browser GPS ('Phone') or vehicle telematics ('Dako')
  const [trackingSource, setTrackingSource] = useState<'Phone' | 'Dako'>('Phone');
  const [dakoStatus, setDakoStatus] = useState<DakoStatusResponse | null>(null);
  // For DAKO sessions: which configured transport method to track
  const [selectedTransportationId, setSelectedTransportationId] = useState<number | null>(null);
  const [otherDeviceSession, setOtherDeviceSession] = useState<TrackingSessionResponse | null>(null);
  const [adminActiveSessions, setAdminActiveSessions] = useState<TrackingSessionResponse[]>([]);
  const [forceStoppingId, setForceStoppingId] = useState<number | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('Admin') ?? false;

  const error = ctxError || localError;

  // Load session list (for history display)
  const loadSessions = useCallback(async () => {
    try {
      const data = await getMySessions();
      setSessions(data);
    } catch (err: any) {
      setLocalError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Load DAKO (vehicle telematics) availability so we can offer it as a source
  useEffect(() => {
    getDakoStatus()
      .then(status => {
        setDakoStatus(status);
        // Pre-select the first DAKO-configured transport method for convenience
        if (status.transportations && status.transportations.length > 0) {
          setSelectedTransportationId(prev => prev ?? status.transportations[0].transportationId);
        }
      })
      .catch(() => setDakoStatus(null));
  }, []);

  // Load all active sessions (admin only)
  const loadAdminActiveSessions = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await getAdminActiveSessions();
      setAdminActiveSessions(data);
    } catch (err: any) {
      console.error('Failed to load admin active sessions:', err);
    }
  }, [isAdmin]);

  useEffect(() => { loadAdminActiveSessions(); }, [loadAdminActiveSessions]);

  // Force-stop any session (admin only)
  const handleForceStop = async (sessionId: number) => {
    if (!window.confirm('Diese Session wirklich beenden? Das laufende Tracking wird sofort gestoppt.')) return;
    setForceStoppingId(sessionId);
    try {
      await adminForceStopSession(sessionId);
      await loadAdminActiveSessions();
      await loadSessions();
      setOtherDeviceSession(null);
    } catch (err: any) {
      setLocalError(err?.response?.data?.error || err.message);
    } finally {
      setForceStoppingId(null);
    }
  };

  // Load plannings for the "new session" form
  useEffect(() => {
    matchdayPlanningApi.getAllPlannings()
      .then(data => {
        setPlannings(data);
        // Auto-select planning from URL parameter (e.g. /tracking?planningId=42)
        const urlPlanningId = searchParams.get('planningId');
        if (urlPlanningId) {
          const pid = Number(urlPlanningId);
          const p = data.find((pl: MatchdayPlanning) => pl.id === pid);
          if (p) {
            setSelectedPlanningId(pid);
            setNewName(p.name);
            const sorted = [...(p.segments || [])].sort((a: PlanningSegment, b: PlanningSegment) => a.sequenceOrder - b.sequenceOrder);
            setSegments(sorted);
            // Clear the URL param so it doesn't re-trigger
            setSearchParams({}, { replace: true });
          }
        }
      })
      .catch(err => console.error('Failed to load plannings:', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for active session by another user/device when planning is selected
  useEffect(() => {
    if (!selectedPlanningId || activeSession) {
      setOtherDeviceSession(null);
      return;
    }
    getActiveSessionForPlanning(selectedPlanningId)
      .then(session => setOtherDeviceSession(session))
      .catch(() => setOtherDeviceSession(null));
  }, [selectedPlanningId, activeSession]);

  // Start tracking via global context (branches on the selected source)
  const handleStart = async () => {
    if (!newName.trim()) return;
    try {
      if (trackingSource === 'Dako') {
        const selectedTransportation = dakoStatus?.transportations?.find(t => t.transportationId === selectedTransportationId);
        const req: StartDakoTrackingRequest = {
          name: newName.trim(),
          expiryHours: 24,
          deviceName: selectedTransportation ? selectedTransportation.name : 'Fahrzeug (DAKO)',
          ...(selectedPlanningId && { planningId: selectedPlanningId }),
          ...(selectedSegmentId && { targetSegmentId: selectedSegmentId }),
          ...(selectedTransportationId && { transportationId: selectedTransportationId }),
        };
        await handleStartDakoSession(req);
      } else {
        const req: StartTrackingRequest = {
          name: newName.trim(),
          expiryHours: 24,
          deviceName: navigator.userAgent.includes('Mobile') ? 'Mobilgerät' : 'Desktop',
          ...(selectedPlanningId && { planningId: selectedPlanningId }),
          ...(selectedSegmentId && { targetSegmentId: selectedSegmentId }),
        };
        await handleStartSession(req, sessionInterval, sessionKeepScreenOn);
      }
      setNewName('');
      setSelectedPlanningId(null);
      setSelectedSegmentId(null);
      setSegments([]);
      await loadSessions();
    } catch (err: any) {
      setLocalError(err.message);
    }
  };

  // Stop tracking via global context
  const handleStop = async () => {
    await handleStopSession();
    await loadSessions();
  };

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#28a745';
      case 'Paused': return '#ffc107';
      case 'Stopped': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Active': return <><Circle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Aktiv</>;
      case 'Paused': return <><Pause size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Pausiert</>;
      case 'Stopped': return <><Circle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Beendet</>;
      default: return <>{status}</>;
    }
  };

  // Badge describing where a session's positions come from
  const renderSourceBadge = (source?: string) => {
    const isDako = source === 'Dako';
    return (
      <span style={{ ...badgeStyle, backgroundColor: isDako ? '#6f42c1' : '#0d6efd', textTransform: 'none' }}>
        {isDako
          ? <><Truck size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Fahrzeug/DAKO</>
          : <><Smartphone size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Handy</>}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={spinnerStyle}></div>
        <p>Lade Tracking-Daten...</p>
      </div>
    );
  }

  return (
    <div className="page-container" style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}><MapPin size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> Live-Tracking</h1>
        <p style={subtitleStyle}>Bus-Position in Echtzeit teilen</p>
      </div>

      {error && (
        <div style={{ padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '8px', marginBottom: '1rem' }}>
          {error}
          <button onClick={() => { clearError(); setLocalError(null); }} style={{ marginLeft: '1rem', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Active session panel */}
      {activeSession ? (
        <div style={{ ...cardStyle, border: '2px solid #28a745', marginBottom: '2rem' }}>
          <div style={cardHeaderStyle}>
            <h3 style={cardTitleStyle}><Circle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> LIVE: {activeSession.name}</h3>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {renderSourceBadge(activeSession.source)}
              <span style={{ ...badgeStyle, backgroundColor: '#28a745' }}>Aktiv</span>
            </div>
          </div>
          {activeSession.transportationName && (
            <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.5rem' }}>
              <Truck size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {activeSession.transportationName}
            </div>
          )}
          {activeSession.deviceName && (
            <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.5rem' }}>
              <Smartphone size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {activeSession.deviceName}
              {activeSession.startedByUserName && <> &middot; {activeSession.startedByUserName}</>}
            </div>
          )}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {lastPosition && (
              <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                <MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {lastPosition.lat.toFixed(5)}, {lastPosition.lon.toFixed(5)} ({posCount} Updates)
              </span>
            )}
            {isSending && <span style={{ fontSize: '0.85rem', color: '#007bff' }}>⏳ Sende...</span>}
          </div>
          {activeSession.shareUrl && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <a
                href={activeSession.shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ flex: 1, padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', color: '#007bff', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
              >
                <Link2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {activeSession.shareUrl}
              </a>
              <button onClick={() => copyLink(activeSession.shareUrl!)} style={btnSecondary}><ClipboardList size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Kopieren</button>
            </div>
          )}

          {/* Zielwechsel während aktiver Session */}
          {activeSegments.length > 0 && (
            <div style={{ border: '2px solid #e9ecef', borderRadius: '8px', overflow: 'hidden', marginBottom: '1rem' }}>
              <div style={{ padding: '0.5rem 0.75rem', backgroundColor: '#cfe2ff', fontWeight: 600, fontSize: '0.9rem', color: '#084298', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span><Target size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Nächstes Ziel wählen:</span>
                {targetUpdating && <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>wird aktualisiert...</span>}
              </div>
              {activeSegments.map((seg, idx) => {
                const isActive = activeSession.targetSegmentId === seg.id;
                const isFirst = idx === 0;
                const isLast = idx === activeSegments.length - 1;
                return (
                  <div
                    key={seg.id}
                    onClick={() => handleChangeTarget(isActive ? null : seg.id)}
                    style={{
                      padding: '0.6rem 0.75rem',
                      cursor: targetUpdating ? 'wait' : 'pointer',
                      borderTop: '1px solid #e9ecef',
                      backgroundColor: isActive ? '#0d6efd' : 'white',
                      color: isActive ? 'white' : '#2c3e50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s',
                      opacity: targetUpdating ? 0.6 : 1,
                    }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>
                      {isActive ? <Bus size={16} /> : isFirst ? <Home size={16} /> : isLast ? <Building2 size={16} /> : <MapPin size={16} />}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: isActive ? 700 : 400 }}>
                        {seg.location}
                      </div>
                      {seg.description && (
                        <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>{seg.description}</div>
                      )}
                    </div>
                    <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                      {seg.scheduledTime}
                    </span>
                    {isActive && <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>→</span>}
                  </div>
                );
              })}
            </div>
          )}

          <button onClick={handleStop} style={btnDanger}>⏹ Tracking beenden</button>
        </div>
      ) : (
        /* Start new session */
        <div style={{ ...cardStyle, marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>Neues Tracking starten</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* Quelle auswählen: Handy (Browser-GPS) oder Fahrzeug/DAKO (Telematik) */}
            <div>
              <label style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.35rem', display: 'block' }}>
                <Radio size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Quelle
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setTrackingSource('Phone')}
                  style={{ ...sourceBtn, ...(trackingSource === 'Phone' ? sourceBtnActive : {}) }}
                >
                  <Smartphone size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Handy
                </button>
                <button
                  type="button"
                  onClick={() => dakoStatus?.configured && setTrackingSource('Dako')}
                  disabled={!dakoStatus?.configured}
                  title={dakoStatus?.configured ? 'Positionen direkt vom Fahrzeug (DAKO)' : 'DAKO ist nicht konfiguriert'}
                  style={{
                    ...sourceBtn,
                    ...(trackingSource === 'Dako' ? sourceBtnActive : {}),
                    ...(!dakoStatus?.configured ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                >
                  <Truck size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Fahrzeug/DAKO
                </button>
              </div>
              {trackingSource === 'Dako' && (
                <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.4rem' }}>
                  Positionen werden serverseitig direkt vom Fahrzeug (DAKO) abgerufen – kein Handy-GPS nötig.
                </div>
              )}
              {/* Transportmittelauswahl für DAKO-Tracking */}
              {trackingSource === 'Dako' && (dakoStatus?.transportations?.length ?? 0) > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.25rem', display: 'block' }}>
                    <Truck size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Transportmittel
                  </label>
                  <select
                    value={selectedTransportationId ?? ''}
                    onChange={(e) => setSelectedTransportationId(e.target.value ? Number(e.target.value) : null)}
                    style={{ width: '100%', padding: '0.6rem', border: '2px solid #e9ecef', borderRadius: '8px', fontSize: '0.95rem', backgroundColor: 'white' }}
                  >
                    {dakoStatus!.transportations.map(t => (
                      <option key={t.transportationId} value={t.transportationId}>
                        {t.name} ({t.vehicleIdent})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {!dakoStatus?.configured && (
                <div style={{ fontSize: '0.8rem', color: '#adb5bd', marginTop: '0.4rem' }}>
                  Fahrzeug/DAKO ist derzeit nicht konfiguriert.
                </div>
              )}
            </div>

            <select
              value={selectedPlanningId ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                const pid = val ? Number(val) : null;
                setSelectedPlanningId(pid);
                setSelectedSegmentId(null);
                if (pid) {
                  const p = plannings.find(p => p.id === pid);
                  if (p) {
                    if (!newName.trim()) setNewName(p.name);
                    // Load segments from the planning
                    const sorted = [...(p.segments || [])].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
                    setSegments(sorted);
                  }
                } else {
                  setSegments([]);
                }
              }}
              style={{ padding: '0.75rem', border: '2px solid #e9ecef', borderRadius: '8px', fontSize: '1rem', backgroundColor: 'white' }}
            >
              <option value="">-- Spieltagsplanung auswählen (optional) --</option>
              {plannings.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} – {new Date(p.date).toLocaleDateString('de-DE')}
                </option>
              ))}
            </select>

            {/* Segment/Ziel-Liste */}
            {segments.length > 0 && (
              <div style={{ border: '2px solid #e9ecef', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f8f9fa', fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50' }}>
                  <MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Ziel auswählen:
                </div>
                {segments.map((seg, idx) => {
                  const isSelected = selectedSegmentId === seg.id;
                  const isFirst = idx === 0;
                  const isLast = idx === segments.length - 1;
                  return (
                    <div
                      key={seg.id}
                      onClick={() => {
                        setSelectedSegmentId(isSelected ? null : seg.id);
                        if (!isSelected) {
                          setNewName(`Fahrt → ${seg.location}`);
                        }
                      }}
                      style={{
                        padding: '0.6rem 0.75rem',
                        cursor: 'pointer',
                        borderTop: '1px solid #e9ecef',
                        backgroundColor: isSelected ? '#d4edda' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <span style={{ fontSize: '1.1rem' }}>
                        {isFirst ? <Home size={16} /> : isLast ? <Building2 size={16} /> : <MapPin size={16} />}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: isSelected ? 600 : 400, color: '#2c3e50' }}>
                          {seg.location}
                        </div>
                        {seg.description && (
                          <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>{seg.description}</div>
                        )}
                      </div>
                      <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                        {seg.scheduledTime}
                      </span>
                      {isSelected && <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tracking-Parameter (nur für Handy-GPS relevant) */}
            {trackingSource === 'Phone' && (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <label style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.25rem', display: 'block' }}><Radio size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Intervall</label>
                <select
                  value={sessionInterval}
                  onChange={(e) => setSessionInterval(Number(e.target.value))}
                  style={{ width: '100%', padding: '0.6rem', border: '2px solid #e9ecef', borderRadius: '8px', fontSize: '0.95rem', backgroundColor: 'white' }}
                >
                  <option value={2}>2 Sek. (Echtzeit)</option>
                  <option value={5}>5 Sek. (Schnell)</option>
                  <option value={10}>10 Sek. (Standard)</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.25rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.95rem', color: '#2c3e50' }}>
                  <input
                    type="checkbox"
                    checked={sessionKeepScreenOn}
                    onChange={(e) => setSessionKeepScreenOn(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <Smartphone size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Display an
                </label>
              </div>
            </div>
            )}

            {/* Warning: another device is already tracking this planning */}
            {otherDeviceSession && (
              <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '8px', border: '1px solid #ffc107', fontSize: '0.9rem' }}>
                <Bus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
                <strong>Tracking bereits aktiv</strong> – gestartet von {otherDeviceSession.startedByUserName || 'unbekannt'} auf {otherDeviceSession.deviceName || 'unbekannt'}.
                {otherDeviceSession.shareUrl && (
                  <> <a href={otherDeviceSession.shareUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#856404', textDecoration: 'underline' }}>Live ansehen</a></>
                )}
                {isAdmin && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      onClick={() => handleForceStop(otherDeviceSession.id)}
                      disabled={forceStoppingId === otherDeviceSession.id}
                      style={{ ...btnBase, backgroundColor: '#dc3545', color: 'white', fontSize: '0.85rem', opacity: forceStoppingId === otherDeviceSession.id ? 0.6 : 1 }}
                    >
                      <XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Session killen
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="z.B. Anreise nach München"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !otherDeviceSession && handleStart()}
                style={{ flex: 1, padding: '0.75rem', border: '2px solid #e9ecef', borderRadius: '8px', fontSize: '1rem' }}
              />
              <button onClick={handleStart} disabled={!newName.trim() || !!otherDeviceSession} style={{ ...btnPrimary, ...(otherDeviceSession ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
                <Circle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Starten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active sessions (admin) */}
      {isAdmin && adminActiveSessions.length > 0 && (
        <>
          <h2 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            <ShieldAlert size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Aktive Sessions (Admin)
          </h2>
          <div className="cards-grid" style={{ ...cardsGridStyle, marginBottom: '2rem' }}>
            {adminActiveSessions.map((s) => (
              <div key={s.id} style={{ ...cardStyle, border: '2px solid #ffc107' }}>
                <div style={cardHeaderStyle}>
                  <h3 style={{ ...cardTitleStyle, fontSize: '1.1rem' }}>{s.name}</h3>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {renderSourceBadge(s.source)}
                    <span style={{ ...badgeStyle, backgroundColor: '#28a745' }}>Aktiv</span>
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                  <Smartphone size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {s.deviceName || 'unbekannt'}
                  {s.startedByUserName && <> &middot; {s.startedByUserName}</>}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                  Gestartet: {new Date(s.startedAt).toLocaleString('de-DE')}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {s.shareUrl && (
                    <a
                      href={s.shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.8rem', color: '#007bff', textDecoration: 'none' }}
                    >
                      <Link2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Live-Link öffnen
                    </a>
                  )}
                  <button
                    onClick={() => handleForceStop(s.id)}
                    disabled={forceStoppingId === s.id}
                    style={{ ...btnBase, backgroundColor: '#dc3545', color: 'white', fontSize: '0.8rem', opacity: forceStoppingId === s.id ? 0.6 : 1 }}
                  >
                    <XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Session killen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Previous sessions */}
      <h2 style={{ color: '#2c3e50', marginBottom: '1rem' }}>Bisherige Sessions</h2>
      {sessions.length === 0 ? (
        <div style={emptyStateStyle}>
          <p>Noch keine Tracking-Sessions vorhanden.</p>
        </div>
      ) : (
        <div className="cards-grid" style={cardsGridStyle}>
          {sessions.map((s) => (
            <div key={s.id} style={cardStyle}>
              <div style={cardHeaderStyle}>
                <h3 style={{ ...cardTitleStyle, fontSize: '1.1rem' }}>{s.name}</h3>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {renderSourceBadge(s.source)}
                  <span style={{ ...badgeStyle, backgroundColor: getStatusColor(s.status) }}>
                    {getStatusText(s.status)}
                  </span>
                </div>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                Gestartet: {new Date(s.startedAt).toLocaleString('de-DE')}
              </div>
              {s.stoppedAt && (
                <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                  Beendet: {new Date(s.stoppedAt).toLocaleString('de-DE')}
                </div>
              )}
              {s.shareUrl && (
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <a
                    href={s.shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.8rem', color: '#007bff', textDecoration: 'none' }}
                  >
                    <Link2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Live-Link öffnen
                  </a>
                  <button onClick={() => copyLink(s.shareUrl!)} style={{ ...btnSecondary, fontSize: '0.8rem' }}>
                    <ClipboardList size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Kopieren
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  maxWidth: '900px',
  margin: '0 auto',
  padding: '2rem',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle: React.CSSProperties = {
  marginBottom: '2rem',
  textAlign: 'center',
};

const titleStyle: React.CSSProperties = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#2c3e50',
  margin: '0 0 0.5rem 0',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '1rem',
  color: '#6c757d',
  margin: 0,
};

const cardsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '1rem',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: '1px solid #e9ecef',
  borderRadius: '12px',
  padding: '1.25rem',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

const cardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '0.75rem',
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: '1.15rem',
  fontWeight: '600',
  color: '#2c3e50',
  margin: 0,
  flex: 1,
};

const badgeStyle: React.CSSProperties = {
  padding: '0.2rem 0.5rem',
  borderRadius: '4px',
  fontSize: '0.7rem',
  fontWeight: '500',
  color: 'white',
  textTransform: 'uppercase',
};

const emptyStateStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  color: '#6c757d',
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  color: '#6c757d',
};

const spinnerStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #007bff',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '1rem',
};

const btnBase: React.CSSProperties = {
  padding: '0.6rem 1.2rem',
  borderRadius: '8px',
  fontSize: '0.95rem',
  fontWeight: '500',
  cursor: 'pointer',
  border: 'none',
  transition: 'all 0.2s',
};

const btnPrimary: React.CSSProperties = {
  ...btnBase,
  backgroundColor: '#dc3545',
  color: 'white',
};

const btnSecondary: React.CSSProperties = {
  ...btnBase,
  backgroundColor: '#f8f9fa',
  color: '#495057',
  border: '1px solid #dee2e6',
};

const sourceBtn: React.CSSProperties = {
  ...btnBase,
  flex: 1,
  minWidth: '130px',
  backgroundColor: 'white',
  color: '#2c3e50',
  border: '2px solid #e9ecef',
};

const sourceBtnActive: React.CSSProperties = {
  backgroundColor: '#cfe2ff',
  border: '2px solid #0d6efd',
  color: '#084298',
  fontWeight: 600,
};

const btnDanger: React.CSSProperties = {
  ...btnBase,
  backgroundColor: '#6c757d',
  color: 'white',
};

export default Tracking;
